import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { googleEnabled } from './proxy-config';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
import LoginPage from './pages/LoginPage';
import LogoutPage from './pages/LogoutPage';
import ConsentPage from './pages/ConsentPage';
import RegisterPage from './pages/RegisterPage';
import TOTPVerifyPage from './pages/TOTPVerifyPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AccountSecurityPage from './pages/AccountSecurityPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import ActiveSessions from './pages/admin/ActiveSessions';
import SecurityEvents from './pages/admin/SecurityEvents';
import ApplicationsPage from './pages/admin/ApplicationsPage';
import Footer from './components/Footer';
import './App.css';

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div className="loading">Loading...</div>;
  if (!user) {
    // 999.17990: bare "/login" dropped the original path+query — a user
    // bounced here from /consent?oauth_state=X (SSO cookie resolved but no
    // frontend token yet) landed on /account post-login and the MCP flow
    // could never finish. Carry the full path+search as ?next=, validated
    // and consumed by LoginPage via the SAME first-party check TOTPVerifyPage/
    // LogoutPage already use for their redirect targets.
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?next=${next}`} />;
  }
  if (adminOnly && user.role !== 'admin') return <Navigate to="/account" />;

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/totp-verify" element={<TOTPVerifyPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* 999.17792 — MCP OAuth consent screen (signed-in users only). */}
      <Route path="/consent" element={
        <ProtectedRoute><ConsentPage /></ProtectedRoute>
      } />

      <Route path="/account" element={
        <ProtectedRoute><AccountSecurityPage /></ProtectedRoute>
      } />

      <Route path="/admin" element={
        <ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute adminOnly><UserManagement /></ProtectedRoute>
      } />
      <Route path="/admin/sessions" element={
        <ProtectedRoute adminOnly><ActiveSessions /></ProtectedRoute>
      } />
      <Route path="/admin/security" element={
        <ProtectedRoute adminOnly><SecurityEvents /></ProtectedRoute>
      } />
      <Route path="/admin/applications" element={
        <ProtectedRoute adminOnly><ApplicationsPage /></ProtectedRoute>
      } />

      <Route path="/" element={<Navigate to="/login" />} />
    </Routes>
  );
}

// googleEnabled now lives in proxy-config so LoginPage gates on the SAME flag —
// it renders <GoogleLogin>, which throws without the provider below (999.5069).

function App() {
  const content = (
    <AuthProvider>
      <div className="app">
        <AppRoutes />
        <Footer />
      </div>
    </AuthProvider>
  );

  if (!googleEnabled) return content;

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      {content}
    </GoogleOAuthProvider>
  );
}

export default App;
